import * as React from 'react';
import { cn } from '../../lib/cn';

export interface FunnelStage {
  label: string;
  value: number;
}

export interface FunnelChartProps {
  data: FunnelStage[];
  /** Format the stage value. Default: compact number. */
  formatValue?: (n: number) => string;
  /** Color for the widest (first) stage; subsequent stages mix toward a lighter tone. */
  startColor?: string;
  endColor?: string;
  emptyMessage?: string;
  className?: string;
  height?: number;
}

const fmtNum = (n: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0, notation: 'compact' }).format(n);

export function FunnelChart({
  data,
  formatValue = fmtNum,
  startColor = 'var(--accent-500)',
  endColor = 'var(--accent-200)',
  emptyMessage = 'No funnel data',
  className,
  height = 320,
}: FunnelChartProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-border bg-bg-surface text-text-muted',
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className={cn('rounded-lg border border-border bg-bg-surface p-4', className)}>
      <div className="flex flex-col gap-3" style={{ height }}>
        {data.map((stage, idx) => {
          const prev = data[idx - 1];
          const conversion = prev && prev.value > 0
            ? Math.round((stage.value / prev.value) * 100)
            : null;
          // The 4% floor keeps a small-but-real stage legible. It must NOT apply to zero: a stage
          // nobody reached would otherwise draw a visible bar, which on a funnel reads as "a few
          // people got here" — the one thing a zero is saying did not happen.
          const widthPct = stage.value <= 0 ? 0 : Math.max((stage.value / max) * 100, 4);
          const t = data.length > 1 ? idx / (data.length - 1) : 0;

          return (
            <div key={stage.label} className="flex items-center gap-3">
              <div className="w-28 shrink-0 truncate text-caption text-text-secondary" title={stage.label}>
                {stage.label}
              </div>
              <div className="flex-1">
                <div
                  className="relative h-9 rounded-r-md transition-all duration-500"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: `color-mix(in oklab, ${startColor} ${(1 - t) * 100}%, ${endColor} ${t * 100}%)`,
                    // The 4px floor exists for the same reason as the 4% one: a small real stage
                    // must stay visible. It must not apply to zero either — setting width to 0%
                    // while leaving minWidth:4 still paints a four-pixel bar, and the value label
                    // inside it overflows. The first fix changed only the percentage, and the test
                    // written with it asserted the declared percentage, so it could not have caught
                    // this: the test restated the change instead of measuring the outcome.
                    minWidth: stage.value <= 0 ? 0 : 4,
                  }}
                >
                  {/* No bar, no label. The label is absolutely positioned inside the bar and the
                      bar's overflow is visible, so on a zero stage it rendered floating over the
                      card in `text-on-brand` — most visibly in dark themes. The count is already in
                      the list beside the chart. */}
                  {stage.value > 0 && (
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-caption font-medium text-text-on-brand">
                      {formatValue(stage.value)}
                    </span>
                  )}
                </div>
              </div>
              {conversion !== null ? (
                <div className="w-16 shrink-0 text-right text-caption text-text-muted">
                  {conversion}% conv.
                </div>
              ) : (
                <div className="w-16 shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
